import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import ToggleSwitch from '../components/ToggleSwitch';

interface PasswordSettings {
  min_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_numbers: boolean;
  require_special_chars: boolean;
  prohibit_easy_to_guess: boolean;
  prohibit_sequential_numbers: boolean;
  max_sequential_numbers: number;
  prohibit_repeated_characters: boolean;
  max_repeated_characters: number;
  prohibit_common_passwords: boolean;
  prohibit_user_info: boolean;
  require_password_rotation: boolean;
  password_rotation_days: number;
  password_expiration_notification_days: number;
}

const AdminPasswordSettings: React.FC = () => {
  const { t } = useTranslation('adminPasswords');
  usePageTitle(t('title'));
  const { user } = useAuth();
  const { setOnSave, setIsSubmitting, setHasUnsavedChanges } = useUnsavedChanges();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [settings, setSettings] = useState<PasswordSettings>({
    min_length: 8,
    require_uppercase: false,
    require_lowercase: false,
    require_numbers: false,
    require_special_chars: false,
    prohibit_easy_to_guess: false,
    prohibit_sequential_numbers: false,
    max_sequential_numbers: 3,
    prohibit_repeated_characters: false,
    max_repeated_characters: 3,
    prohibit_common_passwords: false,
    prohibit_user_info: false,
    require_password_rotation: false,
    password_rotation_days: 90,
    password_expiration_notification_days: 7,
  });

  // Store original settings to detect changes
  const originalSettingsRef = useRef<PasswordSettings | null>(null);
  const settingsRef = useRef(settings);
  const handleSaveRef = useRef<(() => void) | undefined>(undefined);

  // Update refs when settings change
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Load password settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.company_id) {
        setError(t('errors.companyIdNotFound'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await apiService.getCompanyPasswordSettings(user.company_id);
        setSettings(data);
        originalSettingsRef.current = { ...data };
        setError(null);
        setHasUnsavedChanges(false);
      } catch (err: any) {
        console.error('Failed to load password settings:', err);
        setError(err.response?.data?.error || t('errors.loadFailed'));
        toast.error(t('toast.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user?.company_id, setHasUnsavedChanges, t]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!user?.company_id) {
      toast.error(t('errors.companyIdNotFound'));
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Send all settings fields (not just changed ones)
      const settingsToSave: PasswordSettings = {
        min_length: settingsRef.current.min_length,
        require_uppercase: settingsRef.current.require_uppercase,
        require_lowercase: settingsRef.current.require_lowercase,
        require_numbers: settingsRef.current.require_numbers,
        require_special_chars: settingsRef.current.require_special_chars,
        prohibit_easy_to_guess: settingsRef.current.prohibit_easy_to_guess,
        prohibit_sequential_numbers: settingsRef.current.prohibit_sequential_numbers,
        max_sequential_numbers: settingsRef.current.max_sequential_numbers,
        prohibit_repeated_characters: settingsRef.current.prohibit_repeated_characters,
        max_repeated_characters: settingsRef.current.max_repeated_characters,
        prohibit_common_passwords: settingsRef.current.prohibit_common_passwords,
        prohibit_user_info: settingsRef.current.prohibit_user_info,
        require_password_rotation: settingsRef.current.require_password_rotation,
        password_rotation_days: settingsRef.current.password_rotation_days,
        password_expiration_notification_days: settingsRef.current.password_expiration_notification_days,
      };
      
      console.log('💾 Saving password settings:', settingsToSave);
      await apiService.updateCompanyPasswordSettings(user.company_id, settingsToSave);
      
      // Update original settings after successful save
      originalSettingsRef.current = { ...settingsRef.current };
      setHasUnsavedChanges(false);
      
      toast.success(t('toast.saveSuccess'));
      setError(null);
      
      // Reload settings to ensure we have the latest from server
      const freshData = await apiService.getCompanyPasswordSettings(user.company_id);
      setSettings(freshData);
      originalSettingsRef.current = { ...freshData };
    } catch (err: any) {
      console.error('Failed to update password settings:', err);
      const errorMessage = err.response?.data?.error || t('errors.saveFailed');
      setError(errorMessage);
      toast.error(errorMessage);
      throw err; // Re-throw to let unsaved changes context handle it
    } finally {
      setIsSubmitting(false);
    }
  }, [user?.company_id, setIsSubmitting, setHasUnsavedChanges, t]);

  // Update handleSaveRef when handleSave changes
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  // Detect changes and update unsaved changes state
  useEffect(() => {
    if (originalSettingsRef.current === null) {
      // Haven't loaded yet, don't mark as changed
      return;
    }

    const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettingsRef.current);
    setHasUnsavedChanges(hasChanges);
  }, [settings, setHasUnsavedChanges]);

  // Set up unsaved changes tracking
  useEffect(() => {
    setOnSave(() => () => handleSaveRef.current?.());
    return () => setOnSave(null);
  }, [setOnSave]);

  const handleToggle = (key: keyof PasswordSettings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleNumberChange = (key: keyof PasswordSettings, value: number) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
 
    

        <div className="space-y-6">
          {/* Minimum Length */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('minLength.label')}
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('minLength.hint')}
              </p>
            </div>
            <div className="ml-4">
              <input
                type="number"
                min="1"
                max="128"
                value={settings.min_length}
                onChange={(e) => handleNumberChange('min_length', parseInt(e.target.value) || 8)}
                className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Character Type Requirements */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              {t('characterRequirements.title')}
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('characterRequirements.requireUppercase')}
                  </label>
                </div>
                <ToggleSwitch
                  checked={settings.require_uppercase}
                  onChange={() => handleToggle('require_uppercase')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('characterRequirements.requireLowercase')}
                  </label>
                </div>
                <ToggleSwitch
                  checked={settings.require_lowercase}
                  onChange={() => handleToggle('require_lowercase')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('characterRequirements.requireNumbers')}
                  </label>
                </div>
                <ToggleSwitch
                  checked={settings.require_numbers}
                  onChange={() => handleToggle('require_numbers')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('characterRequirements.requireSpecialChars')}
                  </label>
                </div>
                <ToggleSwitch
                  checked={settings.require_special_chars}
                  onChange={() => handleToggle('require_special_chars')}
                />
              </div>
            </div>
          </div>

          {/* Password Restrictions */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              {t('restrictions.title')}
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('restrictions.prohibitEasyToGuess')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('restrictions.prohibitEasyToGuessHint')}
                  </p>
                </div>
                <ToggleSwitch
                  checked={settings.prohibit_easy_to_guess}
                  onChange={() => handleToggle('prohibit_easy_to_guess')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('restrictions.prohibitSequentialNumbers')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('restrictions.prohibitSequentialNumbersHint')}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <ToggleSwitch
                    checked={settings.prohibit_sequential_numbers}
                    onChange={() => handleToggle('prohibit_sequential_numbers')}
                  />
                  {settings.prohibit_sequential_numbers && (
                    <input
                      type="number"
                      min="2"
                      max="10"
                      value={settings.max_sequential_numbers}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 2;
                        handleNumberChange('max_sequential_numbers', Math.max(2, value));
                      }}
                      className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      placeholder={t('restrictions.maxPlaceholder')}
                    />
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('restrictions.prohibitRepeatedChars')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('restrictions.prohibitRepeatedCharsHint')}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <ToggleSwitch
                    checked={settings.prohibit_repeated_characters}
                    onChange={() => handleToggle('prohibit_repeated_characters')}
                  />
                  {settings.prohibit_repeated_characters && (
                    <input
                      type="number"
                      min="2"
                      max="10"
                      value={settings.max_repeated_characters}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 2;
                        handleNumberChange('max_repeated_characters', Math.max(2, value));
                      }}
                      className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      placeholder={t('restrictions.maxPlaceholder')}
                    />
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('restrictions.prohibitCommonPasswords')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('restrictions.prohibitCommonPasswordsHint')}
                  </p>
                </div>
                <ToggleSwitch
                  checked={settings.prohibit_common_passwords}
                  onChange={() => handleToggle('prohibit_common_passwords')}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('restrictions.prohibitUserInfo')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('restrictions.prohibitUserInfoHint')}
                  </p>
                </div>
                <ToggleSwitch
                  checked={settings.prohibit_user_info}
                  onChange={() => handleToggle('prohibit_user_info')}
                />
              </div>
            </div>
          </div>

          {/* Password Expiration */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              {t('expiration.title')}
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('expiration.requireRotation')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('expiration.requireRotationHint')}
                  </p>
                </div>
                <ToggleSwitch
                  checked={settings.require_password_rotation}
                  onChange={() => handleToggle('require_password_rotation')}
                />
              </div>

              {settings.require_password_rotation && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('expiration.expirationDays')}
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('expiration.expirationDaysHint')}
                      </p>
                    </div>
                    <div className="ml-4">
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={settings.password_rotation_days}
                        onChange={(e) => handleNumberChange('password_rotation_days', parseInt(e.target.value) || 90)}
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('expiration.notificationTiming')}
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('expiration.notificationTimingHint')}
                      </p>
                    </div>
                    <div className="ml-4">
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={settings.password_expiration_notification_days}
                        onChange={(e) => handleNumberChange('password_expiration_notification_days', parseInt(e.target.value) || 7)}
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}
    </div>
  );
};

export default AdminPasswordSettings;

